import cn from 'classnames';
import { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { Back, Breadcrumbs } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';

import { useIsPresignMode } from '@/features/presignAuth';

import { useBreadcrumbs } from '../hooks';

import { ControlPanel } from './ControlPanel/ControlPanel';
import styles from './Header.module.scss';
import { HeaderBanners } from './HeaderBanners/HeaderBanners';
import { HeaderSettingsWrapper } from './HeaderSettingsWrapper/HeaderSettingsWrapper';
import { Logo } from './Logo/Logo';

type HeaderProps = {
  fullWidth?: boolean;
  currentPageLabel?: string;
  className?: string;
  back?: boolean;
  onBackButtonClick?: () => void;
};

export const Header: FC<HeaderProps> = (props) => {
  const {
    fullWidth = false,
    className,
    back = false,
    currentPageLabel,
    onBackButtonClick,
  } = props;

  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  const showNotForCommercialUseBanner = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_NotForCommercialUseBanner,
    ),
  );

  const isReportLimitedSession = useAppSelector(
    authModel.selectors.selectIsReportLimitedSession,
  );

  const isPresignMode = useIsPresignMode();
  const isPrintPage = location.pathname.includes('preview');
  const breadCrumbs = useBreadcrumbs(currentPageLabel);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) {
      return;
    }

    const updateHeaderHeight = () => {
      const height = headerElement.offsetHeight;
      document.documentElement.style.setProperty(
        '--actualHeaderHeight',
        `${height}px`,
      );
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className={cn(styles.container, className)}
      data-section="app-header"
    >
      <div className={cn(styles.headerContent, fullWidth && styles.fullWidth)}>
        <Logo className={styles.logo} isClickable={!isPresignMode} />

        <div className={styles.navigation}>
          {back && <Back onClick={onBackButtonClick} />}

          {!back && !isReportLimitedSession && (
            <Breadcrumbs
              breadCrumbs={breadCrumbs}
              isClickable={!isPresignMode}
              isPrintPage={isPrintPage}
            />
          )}
        </div>

        {showNotForCommercialUseBanner && (
          <div className={styles.warning}>
            <FormattedMessage
              id="header.betaWarning"
              defaultMessage="Clinical Testing"
            />
          </div>
        )}

        <HeaderSettingsWrapper className={styles.settingsWrapper}>
          {!isReportLimitedSession && <ControlPanel />}
        </HeaderSettingsWrapper>
      </div>

      <HeaderBanners className={styles.bannersWithMargin} />
    </header>
  );
};
